/**
 * Request deduplication utility - prevents duplicate concurrent requests only
 * No persistent caching, just prevents multiple identical requests from running simultaneously
 */

interface PendingRequest<T> {
  promise: Promise<T>
  timestamp: number
}

class RequestDeduplicator {
  private static instance: RequestDeduplicator
  private pendingRequests: Map<string, PendingRequest<any>> = new Map()
  private readonly REQUEST_TIMEOUT = 30000 // 30 seconds timeout for cleanup
  private cleanupInterval?: ReturnType<typeof setInterval>

  private constructor() {
    // Only start cleanup interval in browser environment
    if (typeof window !== 'undefined') {
      // Cleanup old pending requests periodically
      this.cleanupInterval = setInterval(() => this.cleanup(), this.REQUEST_TIMEOUT)
    }
  }

  public static getInstance(): RequestDeduplicator {
    if (!RequestDeduplicator.instance) {
      RequestDeduplicator.instance = new RequestDeduplicator()
    }
    return RequestDeduplicator.instance
  }

  /**
   * Execute request with deduplication - if same request is in progress, return existing promise
   */
  async deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const existing = this.pendingRequests.get(key)

    // If there's already a pending request for this key, return its promise
    if (existing) {
      return existing.promise
    }

    // Create new request
    const promise = fetcher()
      .then((result) => {
        // Remove from pending requests when completed successfully
        this.pendingRequests.delete(key)
        // Run cleanup after each request to prevent memory buildup
        this.cleanup()
        return result
      })
      .catch((error) => {
        // Remove from pending requests when failed
        this.pendingRequests.delete(key)
        // Run cleanup after each request to prevent memory buildup
        this.cleanup()
        throw error
      })

    // Store the pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    })

    return promise
  }

  /**
   * Clean up old pending requests that might have been orphaned
   */
  private cleanup(): void {
    const now = Date.now()
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > this.REQUEST_TIMEOUT) {
        this.pendingRequests.delete(key)
      }
    }
  }

  /**
   * Get current pending requests count (for debugging)
   */
  getPendingCount(): number {
    return this.pendingRequests.size
  }

  /**
   * Get all pending request keys (for debugging)
   */
  getPendingKeys(): string[] {
    return Array.from(this.pendingRequests.keys())
  }

  /**
   * Clear all pending requests (useful for testing or force refresh)
   */
  clear(): void {
    this.pendingRequests.clear()
  }

  /**
   * Destroy the deduplicator instance and cleanup resources
   * Call this when shutting down or during hot reload in development
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
      this.cleanupInterval = undefined
    }
    this.clear()
  }
}

export const requestDeduplicator = RequestDeduplicator.getInstance()

// Convenience function
export const deduplicateRequest = requestDeduplicator.deduplicate.bind(requestDeduplicator)
